import { ReactNode } from 'react'
import type { MetaEntityKind, SellerType } from '@/config/metadata-schemas.config'
import {
  getMissingRequiredFieldsForResource,
  getResourceFieldsRequiredFirst,
  getResourceValues,
} from '@/utils/metadata-form.utils'
import {
  DEFAULT_META_FIELD_VIEW,
  getFieldsMatchingView,
  isMetaFieldViewActive,
} from '@/utils/metadata-field-view.utils'
import type { MetaFieldView } from '@/utils/metadata-field-view.utils'
import MetadataField from './MetadataField'
import styles from './MetadataManagementTool.module.css'

type Props = {
  title: string
  /** Muted qualifier after the title (a ticket type's parent group name). */
  subtitle?: string
  resourceType: MetaEntityKind
  resourceId: string
  values: MetadataManagementTool.Metadata[]
  sellerType: SellerType
  /** The panel's search + filter; non-matching fields are hidden, not unmounted. */
  view?: MetaFieldView
}

/**
 * One resource instance (a seller, an event, one tier/group, or a globals blob):
 * a collapsible heading and one self-contained MetadataField per schema field
 * that applies to the current seller type. Required fields render first. Each
 * field validates and saves itself, so this component holds no state.
 * While the panel's search/filter is active the section opens if anything
 * matches and hides itself if nothing does.
 */
const MetadataResourceForm = ({
  title,
  subtitle,
  resourceType,
  resourceId,
  values,
  sellerType,
  view = DEFAULT_META_FIELD_VIEW,
}: Props): ReactNode => {
  const fields = getResourceFieldsRequiredFirst(resourceType, sellerType)
  const currentValues = getResourceValues(values, resourceType, resourceId)
  const missingRequired = getMissingRequiredFieldsForResource(
    resourceType,
    resourceId,
    values,
    sellerType,
  )
  const hasGaps = missingRequired.length > 0
  const isFiltering = isMetaFieldViewActive(view)
  const matchingKeys = new Set(
    getFieldsMatchingView(resourceType, resourceId, values, sellerType, view).map((field) => field.key),
  )

  // Nothing in this resource applies to the current seller type — skip the section.
  if (!fields.length) {
    return null
  }

  return (
    <details
      className={styles.Resource}
      open={hasGaps || (isFiltering && matchingKeys.size > 0)}
      hidden={isFiltering && matchingKeys.size === 0}
      data-testid={`metadata-resource-${resourceType}-${resourceId}`}
    >
      <summary className={styles.ResourceSummary}>
        <span className={styles.ResourceTitle}>
          {title}
          {subtitle && (
            <span className={styles.ResourceSubtitle} data-testid="metadata-resource-subtitle">
              {subtitle}
            </span>
          )}
        </span>
        {hasGaps
          ? (
            <span className={styles.ResourceBadge} data-testid="metadata-resource-missing-count">
              {missingRequired.length} required
            </span>
          )
          : (
            <span className={styles.ResourceBadgeOk} data-testid="metadata-resource-complete">
              complete
            </span>
          )}
      </summary>
      <div className={styles.ResourceBody}>
        {fields.map((field) => (
          <MetadataField
            key={field.key}
            schema={field}
            resourceId={resourceId}
            initialValue={currentValues[field.key] ?? ''}
            sellerType={sellerType}
            hidden={!matchingKeys.has(field.key)}
          />
        ))}
      </div>
    </details>
  )
}

export default MetadataResourceForm
